import { useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { LuCopy } from 'react-icons/lu';

import { COPIED_FEEDBACK_DURATION_MS, type CopyableTextProps } from './types';
import { COMPONENTS_NAMESPACE } from '../../locales';
import { cn } from '../../utils/cn';
import { handleEventBehavior } from '../../utils/EventBehavior';
import { Tooltip, TooltipContent, TooltipTrigger } from '../Tooltip';

/**
 * A component that displays text and copies it to clipboard on click.
 *
 * @param text - The text to be copied. If not provided, children will be used.
 * @param children - The content to display. Also used as copy value if text is not provided.
 * @param showIcon - Whether to show the copy icon. Defaults to false.
 * @param iconPosition - Position of the copy icon ('before' or 'after'). Defaults to 'after'.
 * @param showTooltip - Whether to show a "Click to copy" tooltip that changes to "Copied!" on success. Defaults to true.
 * @param onCopy - Callback fired after text is copied.
 * @param className - Additional CSS classes.
 *
 * @example
 * // Basic usage with children
 * <CopyableText>example@email.com</CopyableText>
 *
 * @example
 * // With text prop (copies text, displays children)
 * <CopyableText text="secret-value">Masked Value</CopyableText>
 *
 * @example
 * // With icon and tooltip
 * <CopyableText showIcon showTooltip>example@email.com</CopyableText>
 *
 * @example
 * // With icon before text
 * <CopyableText showIcon iconPosition="before">example@email.com</CopyableText>
 */
export const CopyableText: React.FC<CopyableTextProps> = ({
    children,
    className,
    eventPreventDefault,
    eventStopPropagation,
    iconPosition = 'after',
    onCopy,
    showIcon = false,
    showTooltip = true,
    text,
    ...props
}) => {
    const { t } = useTranslation(COMPONENTS_NAMESPACE);
    const [isCopied, setIsCopied] = useState(false);
    const copyValue = text ?? (typeof children === 'string' ? children : '');

    const copyToClipboard = useCallback(() => {
        if (copyValue) {
            navigator.clipboard.writeText(copyValue);
            setIsCopied(true);
            onCopy?.();

            setTimeout(() => {
                setIsCopied(false);
            }, COPIED_FEEDBACK_DURATION_MS);
        }
    }, [copyValue, onCopy]);

    const handleClick = useCallback(
        (e: React.MouseEvent) => {
            handleEventBehavior(e, { eventPreventDefault, eventStopPropagation });
            copyToClipboard();
        },
        [copyToClipboard, eventPreventDefault, eventStopPropagation],
    );

    const iconElement = showIcon ? (
        <LuCopy className="shrink-0 size-[14px]" data-testid="copyable-text-icon" aria-hidden="true" />
    ) : null;

    const content = (
        <span
            className={cn(
                'inline-flex items-center gap-1.5 cursor-pointer hover:opacity-70 transition-opacity',
                className,
            )}
            data-testid="copyable-text"
            onClick={handleClick}
            role="button"
            tabIndex={0}
            {...props}
        >
            {iconPosition === 'before' && iconElement}
            <span data-testid="copyable-text-content">{children ?? text}</span>
            {iconPosition === 'after' && iconElement}
        </span>
    );

    if (!showTooltip) {
        return content;
    }

    return (
        <Tooltip>
            <TooltipTrigger asChild>{content}</TooltipTrigger>
            <TooltipContent>{isCopied ? t('copyableText.copied') : t('copyableText.clickToCopy')}</TooltipContent>
        </Tooltip>
    );
};

CopyableText.displayName = 'CopyableText';
